import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { downloadCdfBulkBatch } from '@/integrations/mtrManager/client';
import type { MtrBulkScope } from '@/hooks/useCollectsApiData';
import { useEnsureMtrBulkFilter } from '@/hooks/useMtrBulkFilter';
import type { FilterState } from '@/hooks/useFilters';

/**
 * Baixa lotes de CDFs (ZIP) pelo mtr-manager, com o filtro resolvido no agreements. Um download por vez;
 * o progresso (lotes baixados) é local e não persiste entre sessões.
 *
 * Sem analytics: os eventos `mtr_download`/`mtr_download_solicitado` (RN-06/RN-13,
 * GR-712) medem MTR e não podem receber downloads de CDF.
 */
export function useCdfBulkDownload(
  filters: FilterState,
  scope: MtrBulkScope = 'historico',
) {
  const ensureBulkFilter = useEnsureMtrBulkFilter(filters, scope);

  const [isDownloading, setIsDownloading] = useState(false);
  const [activeBatch, setActiveBatch] = useState<number | null>(null);
  const [downloadedBatches, setDownloadedBatches] = useState<Set<number>>(
    new Set(),
  );

  const downloadBatch = useCallback(
    async (batch: number) => {
      if (isDownloading) return;
      setIsDownloading(true);
      setActiveBatch(batch);
      const toastId = toast.loading(`Gerando arquivo do lote ${batch + 1}...`);

      try {
        const { filter: bulkFilter } = await ensureBulkFilter();
        const blob = await downloadCdfBulkBatch(bulkFilter, batch);

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `cdfs-lote-${batch + 1}.zip`;
        anchor.click();
        window.URL.revokeObjectURL(url);

        setDownloadedBatches((prev) => new Set(prev).add(batch));
        toast.success(`Lote ${batch + 1} baixado com sucesso.`, { id: toastId });
      } catch (error) {
        const message =
          error instanceof DOMException && error.name === 'AbortError'
            ? 'Tempo excedido ao gerar o arquivo. Tente novamente.'
            : error instanceof Error
              ? error.message
              : 'Não foi possível gerar o arquivo. Tente novamente.';
        toast.error(message, { id: toastId });
      } finally {
        setIsDownloading(false);
        setActiveBatch(null);
      }
    },
    [ensureBulkFilter, isDownloading],
  );

  const resetProgress = useCallback(() => {
    setDownloadedBatches(new Set());
    setActiveBatch(null);
  }, []);

  return { isDownloading, activeBatch, downloadedBatches, downloadBatch, resetProgress };
}
